'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCachedQuery } from '../core/internal/get-cached-query.js';
import { createSubscriberQueryFn, getSubscriberQueryMetaOptions } from '../core/internal/subscriber-query-fn.js';
/** Existing list/entity subscribers keep aggregate quotes live on reserve patches. */
export function useLiveMarketRows(queryKey, t0, entityQueryKey, projectEntityRow) {
  const $ = _c(42);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fallback = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== queryClient || $[3] !== queryKey) {
    t2 = createSubscriberQueryFn(queryClient, queryKey);
    $[2] = queryClient;
    $[3] = queryKey;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t3 = getSubscriberQueryMetaOptions(queryClient, queryKey);
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["data"];
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== queryKey || $[10] !== t2 || $[11] !== t3) {
    t5 = {
      queryKey,
      queryFn: t2,
      ...t3,
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY,
      notifyOnChangeProps: t4
    };
    $[9] = queryKey;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const query = useQuery(t5);
  let t6;
  if ($[13] !== queryClient || $[14] !== queryKey) {
    t6 = getCachedQuery(queryClient, {
      queryKey
    });
    $[13] = queryClient;
    $[14] = queryKey;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const cachedQuery = t6;
  const error = cachedQuery?.state.error;
  const errorCode = error !== null && typeof error === "object" && "apiCode" in error ? error.apiCode : undefined;
  const blocked = cachedQuery?.meta?.viewerSensitive === true && (errorCode === "CONTENT_ACCESS_BLOCKED" || errorCode === "CONTENT_BLOCKING_UNAVAILABLE");
  const cached = query.data;
  let t7;
  if ($[16] !== blocked || $[17] !== cached?.data) {
    t7 = blocked ? [] : cached?.data;
    $[16] = blocked;
    $[17] = cached?.data;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const cachedData = t7;
  const isNormalized = entityQueryKey !== undefined && cachedData !== undefined && (cachedData.length === 0 || typeof cachedData[0] === "string");
  let t8;
  if ($[19] !== cachedData || $[20] !== isNormalized) {
    t8 = isNormalized ? cachedData : [];
    $[19] = cachedData;
    $[20] = isNormalized;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const ids = t8;
  let t9;
  if ($[22] !== entityQueryKey || $[23] !== ids || $[24] !== queryClient) {
    t9 = entityQueryKey === undefined ? [] : ids.map(id => {
      const entityKey = entityQueryKey(id);
      return {
        queryKey: entityKey,
        queryFn: createSubscriberQueryFn(queryClient, entityKey),
        ...getSubscriberQueryMetaOptions(queryClient, entityKey),
        enabled: false,
        staleTime: Number.POSITIVE_INFINITY,
        notifyOnChangeProps: ["data"]
      };
    });
    $[22] = entityQueryKey;
    $[23] = ids;
    $[24] = queryClient;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t9) {
    t10 = {
      queries: t9,
      combine: collectEntityData
    };
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const entityData = useQueries(t10);
  if (blocked) {
    let t11;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = [];
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    return t11;
  }
  if (cachedData === undefined) {
    return fallback;
  }
  if (!isNormalized || entityQueryKey === undefined) {
    return cachedData;
  }
  if (hasAllEntityRows(entityData) && projectEntityRow === undefined && cached?._indexFields === undefined) {
    return entityData;
  }
  let t11;
  if ($[29] !== cached?._indexFields || $[30] !== entityData || $[31] !== entityQueryKey || $[32] !== ids || $[33] !== projectEntityRow || $[34] !== queryClient) {
    let t12;
    if ($[36] !== cached?._indexFields || $[37] !== entityData || $[38] !== entityQueryKey || $[39] !== projectEntityRow || $[40] !== queryClient) {
      t12 = (id_0, index) => {
        const observed = entityData[index];
        const row = observed ?? queryClient.getQueryData(entityQueryKey(id_0));
        if (row === undefined) {
          return [];
        }
        const projected = projectEntityRow === undefined ? row : projectEntityRow(row);
        const indexFields = cached?._indexFields?.[id_0];
        return indexFields === undefined ? [projected] : [{
          ...projected,
          ...indexFields
        }];
      };
      $[36] = cached?._indexFields;
      $[37] = entityData;
      $[38] = entityQueryKey;
      $[39] = projectEntityRow;
      $[40] = queryClient;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    t11 = ids.flatMap(t12);
    $[29] = cached?._indexFields;
    $[30] = entityData;
    $[31] = entityQueryKey;
    $[32] = ids;
    $[33] = projectEntityRow;
    $[34] = queryClient;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function collectEntityData(results) {
  return results.map(result => result.data);
}
function hasAllEntityRows(rows) {
  return rows.every(row => row !== undefined);
}